import { ExclamationCircleIcon } from '@heroicons/react/solid';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'shared-ui/lib/components/Button';
import { setupQueryConfig } from 'shared-ui/lib/config/query.config';
import {
  usePartnerDashboardCustomersQuery,
  usePartnerDashboardVehiclesQuery,
} from '../../generated/graphql';
import {
  ServiceCallDashboardFilterValues,
  ServiceCallDashboardTab,
  defaultServiceCallDashboardFilter,
  formatDashboardVehicleLabel,
  isServiceCallDashboardFilterActive,
} from './service-call-dashboard-filter.form';

type Props = {
  tab: ServiceCallDashboardTab;
  appliedValues: ServiceCallDashboardFilterValues;
  resultCount?: number;
  onApply: (values: ServiceCallDashboardFilterValues) => void;
};

const inputClassName =
  'appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm';

const labelClassName = 'block text-sm font-medium text-gray-700 mb-1';

export const ServiceCallDashboardFilter = ({
  tab,
  appliedValues,
  resultCount,
  onApply,
}: Props): JSX.Element => {
  const { t } = useTranslation('ServiceCallDashboardScene');
  const [values, setValues] = useState<ServiceCallDashboardFilterValues>(
    defaultServiceCallDashboardFilter()
  );

  const customersQuery = usePartnerDashboardCustomersQuery(setupQueryConfig());
  const vehiclesQuery = usePartnerDashboardVehiclesQuery(
    setupQueryConfig(values.businessId ? { where: { businessId: values.businessId } } : {})
  );

  const customers = customersQuery.data?.partnerCustomers.list ?? [];
  const vehicles = vehiclesQuery.data?.partnerDashboardVehicles ?? [];

  useEffect(() => {
    const loadedVehicles = vehiclesQuery.data?.partnerDashboardVehicles;

    if (!values.vehicleId || vehiclesQuery.loading || !loadedVehicles) {
      return;
    }

    if (!loadedVehicles.some((vehicle) => vehicle.id === values.vehicleId)) {
      setValues((current) => ({ ...current, vehicleId: '' }));
    }
  }, [vehiclesQuery.data, vehiclesQuery.loading, values.vehicleId]);

  const handleChange = (field: keyof ServiceCallDashboardFilterValues, value: string) => {
    setValues((current) => ({ ...current, [field]: value }));
  };

  const handleReset = () => {
    const next = defaultServiceCallDashboardFilter();
    setValues(next);
    onApply(next);
  };

  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        onApply(values);
      }}
    >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <label className={labelClassName} htmlFor="dashboard-filter-business">
              {t('filterBusiness')}
            </label>
            <select
              id="dashboard-filter-business"
              className={inputClassName}
              value={values.businessId}
              onChange={(event) => handleChange('businessId', event.target.value)}
            >
              <option value="">{t('filterAll')}</option>
              {customers.map((customer) => (
                <option key={customer.businessId} value={customer.businessId}>
                  {customer.businessName}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClassName} htmlFor="dashboard-filter-vehicle">
              {t('filterVehicle')}
            </label>
            <select
              id="dashboard-filter-vehicle"
              className={inputClassName}
              value={values.vehicleId}
              onChange={(event) => handleChange('vehicleId', event.target.value)}
            >
              <option value="">{t('filterAll')}</option>
              {vehicles.map((vehicle) => (
                <option key={vehicle.id} value={vehicle.id}>
                  {formatDashboardVehicleLabel(vehicle)}
                </option>
              ))}
            </select>
          </div>
          {tab === 'reminders' ? (
            <>
              <div>
                <label className={labelClassName} htmlFor="dashboard-filter-date-gte">
                  {t('filterDateFrom')}
                </label>
                <input
                  id="dashboard-filter-date-gte"
                  className={inputClassName}
                  type="date"
                  value={values.dateGte}
                  onChange={(event) => handleChange('dateGte', event.target.value)}
                />
              </div>
              <div>
                <label className={labelClassName} htmlFor="dashboard-filter-date-lte">
                  {t('filterDateTo')}
                </label>
                <input
                  id="dashboard-filter-date-lte"
                  className={inputClassName}
                  type="date"
                  value={values.dateLte}
                  onChange={(event) => handleChange('dateLte', event.target.value)}
                />
              </div>
            </>
          ) : (
            <div>
              <label className={labelClassName} htmlFor="dashboard-filter-status">
                {t('filterStatus')}
              </label>
              <select
                id="dashboard-filter-status"
                className={inputClassName}
                value={values.isRead}
                onChange={(event) => handleChange('isRead', event.target.value)}
              >
                <option value="false">{t('statusUnread')}</option>
                <option value="true">{t('statusRead')}</option>
                <option value="">{t('filterAll')}</option>
              </select>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit">{t('filterApply')}</Button>
          <button
            type="button"
            className="inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            onClick={handleReset}
          >
            {t('filterReset')}
          </button>
          {typeof resultCount === 'number' ? (
            isServiceCallDashboardFilterActive(appliedValues, tab) ? (
              <span className="inline-flex items-center text-sm text-gray-color">
                <ExclamationCircleIcon
                  className="h-5 w-5 mr-1.5 flex-shrink-0"
                  aria-hidden="true"
                />
                {t('filterActive')}
                <span className="mx-2">·</span>
                {t('filterResultCount', { count: resultCount })}
              </span>
            ) : (
              <span className="text-sm text-gray-color">
                {t('filterResultCount', { count: resultCount })}
              </span>
            )
          ) : undefined}
        </div>
      </form>
  );
};
